import React from 'react';
import styled, {css} from 'styled-components';
import {mobileOnly} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';
import {ProductCardProps} from './ProductCard';

type ProductCardDescriptionProps = Pick<
  ProductCardProps,
  | 'product'
  | 'quantity'
  | 'trackProductClick'
  | 'additionalBadge'
  | 'sellingMerchant'
  | 'openInNewTab'
>;

export const ProductCardDescription = ({
  product,
  quantity,
  additionalBadge,
  trackProductClick,
  sellingMerchant,
  totalPrice,
  openInNewTab,
}: ProductCardDescriptionProps & {
  totalPrice?: ProductCardProps['totalPrice'];
}) => {
  const hasSecondaryPriceLabel = Boolean(
    totalPrice &&
      ('finalPrice' in totalPrice
        ? totalPrice.finalPrice.secondaryPriceLabel
        : totalPrice.secondaryPriceLabel),
  );

  return (
    <ProductDescriptionContainer data-testid="productDescriptionContainer">
      <InfoWrapper>
        <UnstyledLink
          onClick={trackProductClick}
          data-testid="productPath"
          to={product.path}
          aria-label={undefined}
          openLinkInNewTab={openInNewTab}
        >
          <ItemDescriptionPropertyHeavy data-testid="productBrandName">
            {product.brand}
          </ItemDescriptionPropertyHeavy>
          <ItemDescriptionProperty
            data-testid="productName"
            $reduceNameWidth={hasSecondaryPriceLabel}
          >
            {product.name}
          </ItemDescriptionProperty>
          <ItemDescriptionProperty data-testid="productSize">
            {product.sizeLabel}
          </ItemDescriptionProperty>
          <ItemDescriptionProperty data-testid="productColor">
            {product.colorLabel}
          </ItemDescriptionProperty>
          {quantity.quantity > 1 && (
            <ItemDescriptionProperty data-testid="productQty">
              x{quantity.quantity}
            </ItemDescriptionProperty>
          )}
          {additionalBadge}
        </UnstyledLink>
        {sellingMerchant}
      </InfoWrapper>
    </ProductDescriptionContainer>
  );
};

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ItemDescriptionProperty = styled.div<{$reduceNameWidth?: boolean}>`
  font-weight: ${FONT_WEIGHTS.mediumBold};
  font-size: ${FONT_SIZES.px12};

  ${props => {
    if (props.$reduceNameWidth) {
      return css`
        width: 150px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      `;
    }
  }}

  ${mobileOnly(
    css`
      line-height: 1.5;
    `,
  )}
`;

const ItemDescriptionPropertyHeavy = styled(ItemDescriptionProperty)`
  font-weight: ${FONT_WEIGHTS.heavy};
  margin-bottom: 0.3125rem;
  text-transform: uppercase;
`;

const ProductDescriptionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  flex-wrap: nowrap;
  align-content: center;
  align-items: center;
  width: 100%;
`;
